import { artworkDebugInfoSchema, artworkListItemAuthorSchema, artworkListItemMediaSchema, artworkListItemSchema, artworkSchema, artworkVisibilitySchema, feedMetadataSchema } from "./artworkItem.mjs";
import { decodeBase64Msgpack } from "./util/msgpack.mjs";
import { z } from "zod";
import { oc } from "@orpc/contract";
//#region src/api/orpc/artwork.ts
/**
* The `after` cursor for the trending feed is a base64-encoded msgpack
* containing the next offset (a non-negative integer). Validate it fully here
* so a malformed cursor produces a 400 at the API boundary instead of NaN
* propagating into Redis offsets in `ArtworkTrendingFeedService`.
*/
const trendingCursorSchema = z.string().min(1).regex(/^[A-Za-z0-9+/]+=*$/, "cursor must be base64").refine((value) => {
	let decoded;
	try {
		decoded = decodeBase64Msgpack(value);
	} catch {
		return false;
	}
	return typeof decoded === "number" && Number.isSafeInteger(decoded) && decoded >= 0;
}, { message: "cursor must decode to a non-negative integer offset" });
/**
* The `after` cursor for the daily-ranking feed is a base64-encoded msgpack
* containing `{ likedCount: number, id: bigint }`. Validate fully here so a
* malformed cursor 400s at the API boundary instead of feeding bad values
* into `fetchDailyRankingDedupArtworks`.
*
* The fields encoded here MUST stay in sync with the ORDER BY / tie-breaker
* used by `fetchDailyRankingDedupArtworks`. If that query changes its
* secondary sort key, the cursor schema, encoding, and query filter all
* need to change together — otherwise pagination can skip or duplicate
* items inside a likedCount tie.
*/
const dailyRankingCursorSchema = z.string().min(1).regex(/^[A-Za-z0-9+/]+=*$/, "cursor must be base64").refine((value) => {
	let decoded;
	try {
		decoded = decodeBase64Msgpack(value);
	} catch {
		return false;
	}
	if (decoded == null || typeof decoded !== "object") return false;
	const { likedCount, id } = decoded;
	return typeof likedCount === "number" && Number.isSafeInteger(likedCount) && likedCount >= 0 && typeof id === "bigint" && id >= 0n;
}, { message: "cursor must decode to { likedCount, id }" });
/**
* The `after` cursor for ES-backed feeds is a base64-encoded msgpack of the
* Elasticsearch cursor object (currently `{ sort, id, seed?, s2? }` — see
* `parseGeneralCursorBasedPaginationInput`). The exact field set is an
* implementation detail of `resolveArtworksFromES`, so we intentionally keep
* validation loose: just confirm the cursor is base64-decodable msgpack of a
* non-null object. A garbage cursor still 400s here instead of crashing the
* downstream ES decoder.
*
* Shared by `/feed/following-news` (per-user) and `/feed/newest` (visitor-
* cacheable). Both use the same ES cursor shape because `resolveArtworksFromES`
* is the underlying loader for both.
*/
const esArtworkFeedCursorSchema = z.string().min(1).regex(/^[A-Za-z0-9+/]+=*$/, "cursor must be base64").refine((value) => {
	let decoded;
	try {
		decoded = decodeBase64Msgpack(value);
	} catch {
		return false;
	}
	return decoded != null && typeof decoded === "object" && !Array.isArray(decoded);
}, { message: "cursor must decode to a non-null, non-array object" });
const createArtworkFromMediaInputSchema = z.object({
	mediaId: z.string().min(1, "mediaId is required"),
	title: z.string().max(50).optional(),
	isPrivate: z.boolean().optional(),
	visibility: artworkVisibilitySchema.optional(),
	tags: z.array(z.string()).max(50).optional(),
	tackIds: z.array(z.string()).max(50).optional(),
	hidePrompts: z.boolean().optional(),
	extra: z.record(z.string(), z.unknown()).optional()
});
const createArtworkFromMediaOutputSchema = artworkSchema.extend({ authorId: z.string() });
const batchGetArtworkMediaIdInputSchema = z.object({ artworkIds: z.array(z.string().regex(/^\d+$/, "artworkId must be a numeric string")).min(1).max(50) });
const batchGetArtworkMediaIdOutputSchema = z.object({ items: z.array(z.object({
	artworkId: z.string(),
	mediaId: z.string(),
	thumbnailUrl: z.string().url().optional()
})) });
const getSimilarArtworksInputSchema = z.object({
	artworkId: z.string().regex(/^\d+$/, "artworkId must be a numeric string"),
	first: z.coerce.number().int().positive().max(50).default(20),
	after: z.string().min(1).optional()
});
const getSimilarArtworksOutputSchema = z.object({
	data: z.array(artworkListItemSchema),
	pageInfo: z.object({
		endCursor: z.string().min(1).optional(),
		hasNextPage: z.boolean()
	})
});
const recommendArtworkItemSchema = artworkListItemSchema.extend({ feedMetadata: feedMetadataSchema.nullable() });
const getRecommendArtworksInputSchema = z.object({
	after: z.string().min(1).optional(),
	first: z.coerce.number().int().positive().max(50).default(20)
});
const getRecommendArtworksOutputSchema = z.object({
	data: z.array(recommendArtworkItemSchema),
	pageInfo: z.object({
		endCursor: z.string().min(1).optional(),
		hasNextPage: z.boolean()
	}),
	feedUuid: z.string().optional()
});
const getTrendingArtworksInputSchema = z.object({
	after: trendingCursorSchema.optional(),
	first: z.coerce.number().int().positive().max(50).default(20)
});
const getTrendingArtworksOutputSchema = z.object({
	data: z.array(artworkListItemSchema),
	pageInfo: z.object({
		endCursor: z.string().min(1).optional(),
		hasNextPage: z.boolean()
	})
});
const getDailyRankingArtworksInputSchema = z.object({
	after: dailyRankingCursorSchema.optional(),
	first: z.coerce.number().int().positive().max(50).default(20)
});
const getDailyRankingArtworksOutputSchema = z.object({
	data: z.array(artworkListItemSchema),
	pageInfo: z.object({
		endCursor: z.string().min(1).optional(),
		hasNextPage: z.boolean()
	})
});
const getFollowingNewsArtworksInputSchema = z.object({
	after: esArtworkFeedCursorSchema.optional(),
	first: z.coerce.number().int().positive().max(50).default(20)
});
const getFollowingNewsArtworksOutputSchema = z.object({
	data: z.array(artworkListItemSchema),
	pageInfo: z.object({
		endCursor: z.string().min(1).optional(),
		hasNextPage: z.boolean()
	})
});
const getNewestArtworksInputSchema = z.object({
	after: esArtworkFeedCursorSchema.optional(),
	first: z.coerce.number().int().positive().max(50).default(20)
});
const getNewestArtworksOutputSchema = z.object({
	data: z.array(artworkListItemSchema),
	pageInfo: z.object({
		endCursor: z.string().min(1).optional(),
		hasNextPage: z.boolean()
	})
});
const artworkContract = oc.prefix("/artwork").tag("Artwork").router({
	createFromMedia: oc.route({
		summary: "Create a new artwork from uploaded media",
		description: "Creates a new artwork from user-uploaded media. Requires authentication and Turnstile verification for web clients.",
		method: "POST",
		path: "/from-media"
	}).input(createArtworkFromMediaInputSchema).output(createArtworkFromMediaOutputSchema).errors({
		UNAUTHORIZED: {
			status: 401,
			message: "User not authenticated"
		},
		BAD_REQUEST: {
			status: 400,
			message: "Invalid input"
		},
		MEDIA_NOT_FOUND: {
			status: 404,
			message: "Media not found"
		}
	}),
	batchGetMediaId: oc.route({
		summary: "Batch get media IDs for artworks",
		method: "POST",
		path: "/batch-media-ids"
	}).input(batchGetArtworkMediaIdInputSchema).output(batchGetArtworkMediaIdOutputSchema).errors({}),
	getSimilarArtworks: oc.route({
		summary: "Get similar artworks",
		description: "Returns artworks similar to the given artwork, using vector similarity search on image embeddings.",
		method: "GET",
		path: "/{artworkId}/similar"
	}).input(getSimilarArtworksInputSchema).output(getSimilarArtworksOutputSchema).errors({}),
	getRecommendArtworks: oc.route({
		summary: "Get personalized recommended artworks feed",
		description: "Returns personalized recommended artworks for the authenticated user.",
		method: "GET",
		path: "/recommend"
	}).input(getRecommendArtworksInputSchema).output(getRecommendArtworksOutputSchema).errors({ UNAUTHORIZED: {
		status: 401,
		message: "User not authenticated"
	} }),
	getRecommendArtworksVisitor: oc.route({
		summary: "Get recommended artworks feed for visitors",
		description: "Returns recommended artworks for unauthenticated visitors.",
		method: "GET",
		path: "/recommend-visitor"
	}).input(getRecommendArtworksInputSchema).output(getRecommendArtworksOutputSchema).errors({}),
	getTrendingArtworks: oc.route({
		summary: "Get trending artworks feed",
		description: "Returns the weekly trending artworks feed ranked by a velocity-weighted like score.",
		method: "GET",
		path: "/feed/trending"
	}).input(getTrendingArtworksInputSchema).output(getTrendingArtworksOutputSchema).errors({}),
	getDailyRankingArtworks: oc.route({
		summary: "Get daily-ranking artworks feed",
		description: "Returns the deduplicated daily ranking feed (one artwork per author, ranked by likedCount).",
		method: "GET",
		path: "/daily-ranking"
	}).input(getDailyRankingArtworksInputSchema).output(getDailyRankingArtworksOutputSchema).errors({}),
	getFollowingNewsArtworks: oc.route({
		summary: "Get following-news artworks feed",
		description: "Returns the latest public artwork from authors the current user follows, sorted by createdAt desc. Returns an empty page for unauthenticated requests.",
		method: "GET",
		path: "/feed/following-news"
	}).input(getFollowingNewsArtworksInputSchema).output(getFollowingNewsArtworksOutputSchema).errors({}),
	getNewestArtworks: oc.route({
		summary: "Get newest artworks feed",
		description: "Returns public artworks sorted by createdAt desc. Visitor-cacheable for anonymous callers; logged-in responses skip the edge cache.",
		method: "GET",
		path: "/feed/newest"
	}).input(getNewestArtworksInputSchema).output(getNewestArtworksOutputSchema).errors({})
});
//#endregion
export { artworkContract, artworkDebugInfoSchema, artworkListItemAuthorSchema, artworkListItemMediaSchema, artworkListItemSchema, artworkSchema, artworkVisibilitySchema, batchGetArtworkMediaIdInputSchema, batchGetArtworkMediaIdOutputSchema, createArtworkFromMediaInputSchema, createArtworkFromMediaOutputSchema, feedMetadataSchema, getDailyRankingArtworksInputSchema, getDailyRankingArtworksOutputSchema, getFollowingNewsArtworksInputSchema, getFollowingNewsArtworksOutputSchema, getNewestArtworksInputSchema, getNewestArtworksOutputSchema, getRecommendArtworksInputSchema, getRecommendArtworksOutputSchema, getSimilarArtworksInputSchema, getSimilarArtworksOutputSchema, getTrendingArtworksInputSchema, getTrendingArtworksOutputSchema, recommendArtworkItemSchema };
